import React, { useState } from "react";
import { MessageSquarePlus, Bug, Lightbulb, MessageCircle, X, Send, CheckCircle2 } from "lucide-react";

const API_URL = (process.env.REACT_APP_API_URL || "").replace(/^["']|["']$/g, "").trim().replace(/\/$/, "");

const TYPES = [
  { id: "bug", label: "Report a Bug", icon: Bug, color: "#dc2626", desc: "Something isn't working" },
  { id: "feature", label: "Feature Request", icon: Lightbulb, color: "#f59e0b", desc: "I wish I could..." },
  { id: "feedback", label: "General Feedback", icon: MessageCircle, color: "#2563eb", desc: "Thoughts or suggestions" },
];

export default function FeedbackWidget({ idToken, currentPage }) {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState("type"); // type, form, success
  const [reportType, setReportType] = useState(null);
  const [priority, setPriority] = useState("normal");
  const [description, setDescription] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const reset = () => {
    setStep("type");
    setReportType(null);
    setPriority("normal");
    setDescription("");
    setSubmitting(false);
  };

  const handleClose = () => {
    setOpen(false);
    setTimeout(reset, 300);
  };

  const handleSubmit = async () => {
    if (!description.trim() || description.trim().length < 5) return;
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/support/report`, {
        method: "POST",
        headers: { Authorization: `Bearer ${idToken}`, "Content-Type": "application/json" },
        body: JSON.stringify({
          report_type: reportType,
          priority: reportType === "bug" ? priority : "normal",
          description: description.trim(),
          current_page: currentPage || window.location.pathname,
        }),
      });
      if (res.ok) {
        setStep("success");
      } else {
        alert("Failed to submit. Please try again.");
      }
    } catch {
      alert("Network error. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {/* Floating trigger button */}
      <button
        onClick={() => setOpen(true)}
        style={{
          position: "fixed", bottom: "24px", right: "24px", zIndex: 9999,
          width: "48px", height: "48px", borderRadius: "50%",
          background: "linear-gradient(135deg, #6366f1, #4f46e5)",
          border: "none", cursor: "pointer", display: "flex",
          alignItems: "center", justifyContent: "center",
          boxShadow: "0 4px 12px rgba(99,102,241,0.4)",
          transition: "transform 0.2s, box-shadow 0.2s",
        }}
        onMouseEnter={e => { e.currentTarget.style.transform = "scale(1.1)"; }}
        onMouseLeave={e => { e.currentTarget.style.transform = "scale(1)"; }}
        title="Report issue or give feedback"
      >
        <MessageSquarePlus size={22} color="#fff" />
      </button>

      {/* Modal overlay */}
      {open && (
        <div style={{
          position: "fixed", inset: 0, zIndex: 10000,
          background: "rgba(0,0,0,0.4)", display: "flex",
          alignItems: "center", justifyContent: "center",
          padding: "20px",
        }} onClick={handleClose}>
          <div
            style={{
              background: "#fff", borderRadius: "16px", width: "100%",
              maxWidth: "420px", padding: "24px", position: "relative",
              boxShadow: "0 20px 60px rgba(0,0,0,0.2)",
            }}
            onClick={e => e.stopPropagation()}
          >
            {/* Close button */}
            <button onClick={handleClose} style={{
              position: "absolute", top: "12px", right: "12px",
              background: "none", border: "none", cursor: "pointer", color: "#94a3b8",
            }}>
              <X size={20} />
            </button>

            {/* Step: Choose type */}
            {step === "type" && (
              <>
                <h3 style={{ margin: "0 0 4px", fontSize: "18px", fontWeight: 700, color: "#0f172a" }}>
                  How can we help?
                </h3>
                <p style={{ margin: "0 0 16px", fontSize: "13px", color: "#64748b" }}>
                  Select the type of report
                </p>
                <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                  {TYPES.map(t => (
                    <button
                      key={t.id}
                      onClick={() => { setReportType(t.id); setStep("form"); }}
                      style={{
                        display: "flex", alignItems: "center", gap: "12px",
                        padding: "14px 16px", borderRadius: "12px",
                        border: "1px solid #e2e8f0", background: "#fafafa",
                        cursor: "pointer", textAlign: "left", transition: "all 0.15s",
                      }}
                      onMouseEnter={e => { e.currentTarget.style.borderColor = t.color; e.currentTarget.style.background = "#fff"; }}
                      onMouseLeave={e => { e.currentTarget.style.borderColor = "#e2e8f0"; e.currentTarget.style.background = "#fafafa"; }}
                    >
                      <t.icon size={20} color={t.color} />
                      <div>
                        <div style={{ fontSize: "14px", fontWeight: 600, color: "#0f172a" }}>{t.label}</div>
                        <div style={{ fontSize: "12px", color: "#64748b" }}>{t.desc}</div>
                      </div>
                    </button>
                  ))}
                </div>
              </>
            )}

            {/* Step: Form */}
            {step === "form" && (
              <>
                <h3 style={{ margin: "0 0 16px", fontSize: "18px", fontWeight: 700, color: "#0f172a" }}>
                  {TYPES.find(t => t.id === reportType)?.label}
                </h3>

                {reportType === "bug" && (
                  <div style={{ marginBottom: "12px" }}>
                    <label style={{ fontSize: "12px", fontWeight: 600, color: "#475569", display: "block", marginBottom: "6px" }}>
                      Priority
                    </label>
                    <div style={{ display: "flex", gap: "8px" }}>
                      {["low", "normal", "critical"].map(p => (
                        <button
                          key={p}
                          onClick={() => setPriority(p)}
                          style={{
                            padding: "6px 14px", borderRadius: "8px", fontSize: "12px", fontWeight: 600,
                            border: priority === p ? "2px solid" : "1px solid #e2e8f0",
                            borderColor: priority === p ? (p === "critical" ? "#dc2626" : p === "normal" ? "#f59e0b" : "#64748b") : "#e2e8f0",
                            background: priority === p ? (p === "critical" ? "#fef2f2" : p === "normal" ? "#fffbeb" : "#f8fafc") : "#fff",
                            color: p === "critical" ? "#dc2626" : p === "normal" ? "#d97706" : "#64748b",
                            cursor: "pointer",
                          }}
                        >
                          {p.charAt(0).toUpperCase() + p.slice(1)}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <div style={{ marginBottom: "16px" }}>
                  <label style={{ fontSize: "12px", fontWeight: 600, color: "#475569", display: "block", marginBottom: "6px" }}>
                    Description
                  </label>
                  <textarea
                    value={description}
                    onChange={e => setDescription(e.target.value)}
                    placeholder={
                      reportType === "bug" ? "What happened? What did you expect to happen?"
                      : reportType === "feature" ? "Describe the feature you'd like to see..."
                      : "Share your thoughts..."
                    }
                    style={{
                      width: "100%", minHeight: "120px", padding: "12px",
                      borderRadius: "10px", border: "1px solid #e2e8f0",
                      fontSize: "14px", resize: "vertical", outline: "none",
                      fontFamily: "inherit",
                    }}
                    onFocus={e => { e.target.style.borderColor = "#6366f1"; }}
                    onBlur={e => { e.target.style.borderColor = "#e2e8f0"; }}
                    autoFocus
                  />
                  <div style={{ fontSize: "11px", color: "#94a3b8", marginTop: "4px" }}>
                    {description.length}/2000 characters
                  </div>
                </div>

                <div style={{ display: "flex", gap: "8px", justifyContent: "flex-end" }}>
                  <button onClick={() => { setStep("type"); setDescription(""); }} style={{
                    padding: "10px 16px", borderRadius: "10px", fontSize: "13px",
                    border: "1px solid #e2e8f0", background: "#fff", cursor: "pointer", color: "#64748b",
                  }}>
                    Back
                  </button>
                  <button
                    onClick={handleSubmit}
                    disabled={submitting || description.trim().length < 5}
                    style={{
                      padding: "10px 20px", borderRadius: "10px", fontSize: "13px", fontWeight: 600,
                      border: "none", background: submitting || description.trim().length < 5 ? "#cbd5e1" : "#4f46e5",
                      color: "#fff", cursor: submitting ? "wait" : "pointer",
                      display: "flex", alignItems: "center", gap: "6px",
                    }}
                  >
                    <Send size={14} />
                    {submitting ? "Sending..." : "Submit"}
                  </button>
                </div>
              </>
            )}

            {/* Step: Success */}
            {step === "success" && (
              <div style={{ textAlign: "center", padding: "20px 0" }}>
                <CheckCircle2 size={48} color="#10b981" style={{ marginBottom: "12px" }} />
                <h3 style={{ margin: "0 0 8px", fontSize: "18px", fontWeight: 700, color: "#0f172a" }}>
                  Thank you!
                </h3>
                <p style={{ fontSize: "14px", color: "#64748b", margin: "0 0 20px" }}>
                  Your {reportType === "bug" ? "report" : reportType === "feature" ? "request" : "feedback"} has been submitted.
                  Our team will review it shortly.
                </p>
                <button onClick={handleClose} style={{
                  padding: "10px 24px", borderRadius: "10px", fontSize: "13px", fontWeight: 600,
                  border: "none", background: "#4f46e5", color: "#fff", cursor: "pointer",
                }}>
                  Close
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
